import { useState } from 'react';
import { Wifi, Heart, ShoppingBag, UserCircle, Search, ChevronLeft } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function MyHotSpots() {
  const navigate = useNavigate();
  const { theme } = useApp();
  const { t } = useTranslation();
  
  const [hotspots, setHotspots] = useState([
    {
      id: 1,
      name: 'Cozy Corner Cafe',
      location: '123 Main Street, Anytown',
      description: 'A cozy spot with great coffee and fast internet',
      pricing: '₦5/hour',
      availability: 'Available'
    },
    {
      id: 2,
      name: 'Tech Hub Workspace',
      location: '456 Innovation Drive, Anytown',
      description: 'A modern workspace for tech professionals',
      pricing: '₦20/day',
      availability: 'Available'
    },
    {
      id: 3,
      name: 'Community Library',
      location: '789 Book Lane, Anytown',
      description: 'A quiet place to study and connect',
      pricing: 'Free',
      availability: 'Unavailable'
    }
  ]);

  const hotspotContent = {
    1: {
      location: t('operator.hotspotsPage.items.1.location', { defaultValue: '123 Main Street, Anytown' }),
      description: t('operator.hotspotsPage.items.1.description', { defaultValue: 'A cozy spot with great coffee and fast internet' }),
      pricing: t('operator.hotspotsPage.items.1.pricing', { defaultValue: '5 USD/hour' }),
    },
    2: {
      location: t('operator.hotspotsPage.items.2.location', { defaultValue: '456 Innovation Drive, Anytown' }),
      description: t('operator.hotspotsPage.items.2.description', { defaultValue: 'A modern workspace for tech professionals' }),
      pricing: t('operator.hotspotsPage.items.2.pricing', { defaultValue: '20 USD/day' }),
    },
    3: {
      location: t('operator.hotspotsPage.items.3.location', { defaultValue: '789 Book Lane, Anytown' }),
      description: t('operator.hotspotsPage.items.3.description', { defaultValue: 'A quiet place to study and connect' }),
      pricing: t('operator.hotspotsPage.items.3.pricing', { defaultValue: 'Free' }),
    },
  } as const;

  const handleSubmit = () => {
    navigate("/operator/Vouchers");   
  };

  const handleEdit = (id) => {
    console.log('Edit hotspot:', id);
  };

  const handleDelete = (id) => {
    setHotspots(hotspots.filter(h => h.id !== id));
  };

  return (

     <>
      <Helmet>
        <title>{t('operator.hotspotsPage.meta.title', { defaultValue: 'My Hotspots | KonectX' })}</title>
        <meta
          name="description"
          content={t('operator.hotspotsPage.meta.description', { defaultValue: 'View and manage all your KonectX hotspots. Monitor activity, status, and performance of your decentralized network devices.' })}
        />
        <meta
          name="keywords"
          content={t('operator.hotspotsPage.meta.keywords', { defaultValue: 'KonectX, my hotspots, hotspot management, decentralized network, KXT token' })}
        />
        <meta property="og:title" content={t('operator.hotspotsPage.meta.ogTitle', { defaultValue: 'My Hotspots - KonectX' })} />
        <meta
          property="og:description"
          content={t('operator.hotspotsPage.meta.description', { defaultValue: 'View and manage all your KonectX hotspots. Monitor activity, status, and performance of your decentralized network devices.' })}
        />
      </Helmet>

    <div className={`min-h-screen ${
      theme === 'dark' 
        ? 'bg-black' 
        : 'bg-gradient-to-br from-purple-50 to-blue-50'
    }`}>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className={`text-4xl font-extrabold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {t('operator.hotspotsPage.title', { defaultValue: 'My Hotspots' })}
          </h1>
          <button
            onClick={handleSubmit}
            className="px-6 py-3 bg-black dark:bg-blue-600  text-white dark:text-black text-base font-bold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            {t('operator.hotspotsPage.addNew', { defaultValue: 'Add New Hotspot' })}
          </button>
        </div>

        {/* Hotspots Display - Desktop Table, Mobile Cards */}
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className={`hidden md:block rounded-lg shadow-lg border-2 overflow-hidden ${
            theme === 'dark'
              ? 'bg-[#333436] border-gray-700'
              : 'bg-white border-gray-300'
          }`}>
            <table className="w-full">
              <thead className={`border-b-2 ${
                theme === 'dark'
                  ? 'bg-[#2a2a2c] border-gray-700'
                  : 'bg-gray-100 border-gray-300'
              }`}>
                <tr>
                  <th className={`px-6 py-5 text-left text-sm font-extrabold uppercase tracking-wider ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('operator.hotspotsPage.columns.name', { defaultValue: 'Name' })}
                  </th>
                  <th className={`px-6 py-5 text-left text-sm font-extrabold uppercase tracking-wider ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('operator.hotspotsPage.columns.location', { defaultValue: 'Location' })}
                  </th>
                  <th className={`px-6 py-5 text-left text-sm font-extrabold uppercase tracking-wider ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('operator.hotspotsPage.columns.description', { defaultValue: 'Description' })}
                  </th>
                  <th className={`px-6 py-5 text-left text-sm font-extrabold uppercase tracking-wider ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('operator.hotspotsPage.columns.pricing', { defaultValue: 'Pricing' })}
                  </th>
                  <th className={`px-6 py-5 text-left text-sm font-extrabold uppercase tracking-wider ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('operator.hotspotsPage.columns.availability', { defaultValue: 'Availability' })}
                  </th>
                  <th className={`px-6 py-5 text-left text-sm font-extrabold uppercase tracking-wider ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('operator.hotspotsPage.columns.actions', { defaultValue: 'Actions' })}
                  </th>
                </tr>
              </thead>
              <tbody className={theme === 'dark' ? 'bg-[#333436]' : 'bg-white'}>
                {hotspots.map((hotspot, index) => (
                  <tr 
                    key={hotspot.id} 
                    className={`transition-colors border-b-2 ${
                      theme === 'dark'
                        ? 'hover:bg-[#3a3a3c] border-gray-700'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}
                    style={{ borderBottom: index === hotspots.length - 1 ? 'none' : '' }}
                  >
                    <td className="px-6 py-6">
                      <div className={`text-base font-extrabold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {hotspot.name}
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className={`text-sm font-bold ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {hotspotContent[hotspot.id as 1 | 2 | 3]?.location ?? hotspot.location}
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className={`text-sm font-semibold max-w-xs ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {hotspotContent[hotspot.id as 1 | 2 | 3]?.description ?? hotspot.description}
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                      <div className={`text-base font-extrabold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {hotspotContent[hotspot.id as 1 | 2 | 3]?.pricing ?? hotspot.pricing}
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                      <span className={`px-3 py-2 inline-flex text-sm font-extrabold rounded-full ${
                        hotspot.availability === 'Available' 
                          ? theme === 'dark'
                            ? 'bg-green-900 text-green-200'
                            : 'bg-green-100 text-green-800'
                          : theme === 'dark'
                            ? 'bg-red-900 text-red-200'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {hotspot.availability === 'Available'
                          ? t('operator.hotspotsPage.status.available', { defaultValue: 'Available' })
                          : t('operator.hotspotsPage.status.unavailable', { defaultValue: 'Unavailable' })}
                      </span>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEdit(hotspot.id)}
                        className={`font-extrabold mr-3 transition-colors ${
                          theme === 'dark'
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-blue-600 hover:text-blue-800'
                        }`}
                      >
                        {t('operator.hotspotsPage.actions.edit', { defaultValue: 'Edit' })}
                      </button>
                      <span className={`font-bold ${
                        theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        |
                      </span>
                      <button
                        onClick={() => handleDelete(hotspot.id)}
                        className={`font-extrabold ml-3 transition-colors ${
                          theme === 'dark'
                            ? 'text-red-400 hover:text-red-300'
                            : 'text-red-600 hover:text-red-800'
                        }`}
                      >
                        {t('operator.hotspotsPage.actions.delete', { defaultValue: 'Delete' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {hotspots.map((hotspot) => (
              <div 
                key={hotspot.id} 
                className={`rounded-lg shadow-lg border-2 p-6 ${
                  theme === 'dark'
                    ? 'bg-[#333436] border-gray-700'
                    : 'bg-white border-gray-300'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <h3 className={`text-xl font-extrabold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {hotspot.name}
                    </h3>
                    <span className={`px-3 py-1.5 text-xs font-extrabold rounded-full whitespace-nowrap ${
                      hotspot.availability === 'Available' 
                        ? theme === 'dark'
                          ? 'bg-green-900 text-green-200'
                          : 'bg-green-100 text-green-800'
                        : theme === 'dark'
                          ? 'bg-red-900 text-red-200'
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {hotspot.availability === 'Available'
                        ? t('operator.hotspotsPage.status.available', { defaultValue: 'Available' })
                        : t('operator.hotspotsPage.status.unavailable', { defaultValue: 'Unavailable' })}
                    </span>
                  </div>
                  
                  <div>
                    <p className={`text-xs font-extrabold uppercase mb-1.5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('operator.hotspotsPage.columns.location', { defaultValue: 'Location' })}
                    </p>
                    <p className={`text-sm font-bold ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {hotspotContent[hotspot.id as 1 | 2 | 3]?.location ?? hotspot.location}
                    </p>
                  </div>
                  
                  <div>
                    <p className={`text-xs font-extrabold uppercase mb-1.5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('operator.hotspotsPage.columns.description', { defaultValue: 'Description' })}
                    </p>
                    <p className={`text-sm font-semibold ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {hotspotContent[hotspot.id as 1 | 2 | 3]?.description ?? hotspot.description}
                    </p>
                  </div>
                  
                  <div>
                    <p className={`text-xs font-extrabold uppercase mb-1.5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('operator.hotspotsPage.columns.pricing', { defaultValue: 'Pricing' })}
                    </p>
                    <p className={`text-base font-extrabold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {hotspotContent[hotspot.id as 1 | 2 | 3]?.pricing ?? hotspot.pricing}
                    </p>
                  </div>
                  
                  <div className={`flex space-x-3 pt-3 border-t-2 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <button
                      onClick={() => handleEdit(hotspot.id)}
                      className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-extrabold rounded-lg transition-all duration-200"
                    >
                      {t('operator.hotspotsPage.actions.edit', { defaultValue: 'Edit' })}
                    </button>
                    <button
                      onClick={() => handleDelete(hotspot.id)}
                      className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-extrabold rounded-lg transition-all duration-200"
                    >
                      {t('operator.hotspotsPage.actions.delete', { defaultValue: 'Delete' })}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
    </>
  );
}
